import type { StudioView } from "./studio";

/**
 * URL shape of the studio views. Each view owns a route so a tab can be
 * bookmarked, shared, and reached with the browser back/forward buttons:
 *
 *   /studio          -> roll     (the piano-roll itself)
 *   /studio/harmony  -> harmony
 *   /studio/chords   -> chords
 *
 * The pathname is the single source of truth: `StudioPage` mirrors it into
 * `Studio.setView`, and every view switch navigates instead of writing the
 * observable directly (see `useStudioView`). The routes themselves render
 * `null`, the studio content lives in the `(main)` layout so switching tabs
 * never remounts the piano-roll.
 *
 * ## Ce qui n'a pas de route, et pourquoi
 *
 * Les parcours. Ils ne sont pas des onglets mais des engagements (voir
 * `Engagement`), et une route ne leur apporterait rien ici : le morceau ne vit
 * qu'en mémoire, donc une URL de parcours partagée atterrit sur un écran vide.
 * `/studio/level` l'a été et le faisait — un lien qui promettait un niveau et
 * rendait zéro unité, parce que la vue revenait du pathname et le niveau de
 * nulle part. Le retour à la fiche se fait par son propre geste.
 */

export const STUDIO_BASE_PATH = "/studio";

/** Route segment of each view. `roll` has none, it *is* the studio, so
 *  `/studio` stays the canonical piano-roll URL. The total record makes
 *  adding a `StudioView` without a segment a build error. */
const SEGMENT_BY_VIEW: Record<StudioView, string | null> = {
  roll: null,
  harmony: "harmony",
  chords: "chords",
  lab: "lab",
};

const VIEW_BY_SEGMENT = new Map<string, StudioView>(
  Object.entries(SEGMENT_BY_VIEW).flatMap(([view, segment]) =>
    segment === null ? [] : [[segment, view as StudioView]],
  ),
);

/** Segments accepted by the `/studio/[view]` route. */
export const STUDIO_VIEW_SEGMENTS: readonly string[] = [...VIEW_BY_SEGMENT.keys()];

/** Route of a studio view. */
export function studioViewPath(view: StudioView): string {
  const segment = SEGMENT_BY_VIEW[view];

  if (segment === null) {
    return STUDIO_BASE_PATH;
  }

  return `${STUDIO_BASE_PATH}/${segment}`;
}

/**
 * View encoded by a pathname, `null` when the pathname is not a studio
 * route at all. Callers then leave the current view untouched, navigating
 * to `/learn` must not reset the studio.
 */
export function studioViewFromPathname(pathname: string): StudioView | null {
  // `/` only redirects to `/studio` (see app/page.tsx). Accept it so the
  // canvas isn't hidden for the frame the redirect takes.
  if (pathname === STUDIO_BASE_PATH || pathname === "/") {
    return "roll";
  }

  if (!pathname.startsWith(`${STUDIO_BASE_PATH}/`)) {
    return null;
  }

  return VIEW_BY_SEGMENT.get(pathname.slice(STUDIO_BASE_PATH.length + 1)) ?? null;
}

/** Whether the studio canvas owns the viewport on this pathname. */
export function isStudioPathname(pathname: string): boolean {
  return studioViewFromPathname(pathname) !== null;
}
